import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  X, Upload, CheckCircle, AlertCircle, CreditCard, Smartphone, DollarSign, Bitcoin, Building2, Copy, Check, Loader2, ShieldCheck
} from 'lucide-react';
import { api } from '@/lib/api.ts';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock.js';

const PAYMENT_TYPE_ICONS = {
  bank_transfer: Building2,
  mobile_payment: Smartphone,
  zelle: DollarSign,
  paypal: CreditCard,
  binance: Bitcoin,
  reserve: DollarSign,
  crypto: Bitcoin,
  other: CreditCard,
};

export function SubscriptionPaymentModal({ isOpen, onClose, plan, paymentMethods = [] }) {
  // Bloquear scroll del body cuando el modal está abierto
  useBodyScrollLock(isOpen);
  
  const [step, setStep] = useState(1);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [formData, setFormData] = useState({ receipt_image: null, reference_number: '', notes: '' });
  const [preview, setPreview] = useState(null);
  const [uploadStatus, setUploadStatus] = useState({ isLoading: false, message: '', isError: false, errorMessage: '' });
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Usar tasas del SuperAdmin para mostrar precio en Bs
  const { data: bcvRateData } = useQuery({
    queryKey: ['superadmin-rates'],
    queryFn: async () => (await api.get('/api/exchange-rates/latest/')).data,
    enabled: isOpen,
  });

  const getBsPrice = (usdPrice) => {
    // Usar la tasa USD del SuperAdmin (cuántos Bs por 1 USD)
    const bcvRate = bcvRateData?.USD?.rate || 0;
    return usdPrice * bcvRate;
  };

  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) { console.error('Error al copiar:', err); }
  };

  const copyAllDetails = async (method) => {
    let textToCopy = `${method.name}\nTipo: ${method.type_display}\n\n`;
    if (method.type === 'bank_transfer') {
      if (method.config.banco) textToCopy += `Banco: ${method.config.banco}\n`;
      if (method.config.tipo_cuenta) textToCopy += `Tipo: ${method.config.tipo_cuenta}\n`;
      if (method.config.numero_cuenta) textToCopy += `Cuenta: ${method.config.numero_cuenta}\n`;
      if (method.config.titular) textToCopy += `Titular: ${method.config.titular}\n`;
      if (method.config.rif) textToCopy += `RIF: ${method.config.rif}\n`;
    } else if (method.type === 'mobile_payment') {
      if (method.config.banco) textToCopy += `Banco: ${method.config.banco}\n`;
      if (method.config.telefono) textToCopy += `Teléfono: ${method.config.telefono}\n`;
      if (method.config.cedula) textToCopy += `Cédula: ${method.config.cedula}\n`;
    } else if (method.type === 'zelle' || method.type === 'paypal') {
      if (method.config.email) textToCopy += `Email: ${method.config.email}\n`;
    } else if (method.type === 'crypto') {
      if (method.config.coin) textToCopy += `Moneda: ${method.config.coin}\n`;
      if (method.config.network) textToCopy += `Red: ${method.config.network}\n`;
      if (method.config.address) textToCopy += `Dirección: ${method.config.address}\n`;
    }
    if (method.instructions) textToCopy += `\nInstrucciones: ${method.instructions}`;
    await copyToClipboard(textToCopy, 'all');
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert("El archivo es demasiado grande. El límite es 10MB.");
        return;
      }
      setFormData({ ...formData, receipt_image: file });
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.receipt_image || !selectedMethod || !plan) {
      alert("Por favor, completa todos los campos requeridos.");
      return;
    }
    if (!bcvRateData?.USD?.rate || bcvRateData.USD.rate <= 0) {
      setUploadStatus({ isLoading: false, isError: true, errorMessage: "Error: No se pudo obtener la tasa de cambio actual." });
      return;
    }

    // Mostrar modal de confirmación
    setShowConfirmModal(true);
  };

  const handleConfirmPayment = async () => {
    setShowConfirmModal(false);
    setUploadStatus({ isLoading: true, message: 'Iniciando transmisión...', isError: false, errorMessage: '' });

    try {
      setUploadStatus(prev => ({ ...prev, message: 'Solicitando enlace seguro...' }));
      const generateUrlResponse = await api.post('/api/subscription/payment/generate-upload-url/', {
        fileName: formData.receipt_image.name, contentType: formData.receipt_image.type,
      });
      const { uploadURL, filePath } = generateUrlResponse.data;

      setUploadStatus(prev => ({ ...prev, message: 'Subiendo evidencia...' }));
      const uploadResponse = await fetch(uploadURL, {
        method: 'PUT', headers: { 'Content-Type': formData.receipt_image.type }, body: formData.receipt_image,
      });

      if (!uploadResponse.ok) throw new Error('Error en subida.');

      setUploadStatus(prev => ({ ...prev, message: 'Verificando transacción...' }));
      await api.post('/api/subscription/payment/register-proof/', {
        plan_id: plan.id, payment_method_id: selectedMethod.id, filePath: filePath,
        reference_number: formData.reference_number, notes: formData.notes,
        usd_amount: plan.price, amount_in_ves: getBsPrice(plan.price), bcv_rate: bcvRateData.USD.rate
      });

      setUploadStatus({ isLoading: false, message: '', isError: false });
      setStep(3);

    } catch (error) {
      console.error("Error:", error);
      const errorMessage = error.response?.data?.error || 'Error inesperado.';
      setUploadStatus({ isLoading: false, message: '', isError: true, errorMessage: `Error: ${errorMessage}` });
    }
  };

  const handleClose = () => {
    setStep(1); setSelectedMethod(null);
    setFormData({ receipt_image: null, reference_number: '', notes: '' });
    setPreview(null);
    setUploadStatus({ isLoading: false, message: '', isError: false, errorMessage: '' });
    setShowConfirmModal(false);
    onClose();
  };

  if (!isOpen) return null;

  // Estilos
  const inputClass = "w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/20 outline-none transition-all";
  const labelClass = "block text-[10px] font-bold uppercase tracking-widest text-white/40 mb-2";

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-xl animate-in fade-in duration-300 p-4">
      <div className="relative w-full max-w-2xl bg-[#0F0F0F] border border-white/10 rounded-[2rem] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b border-white/5 bg-black/20 backdrop-blur-md sticky top-0 z-10">
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
               <ShieldCheck className="h-5 w-5 text-indigo-400" /> Pago de Suscripción
            </h2>
            <p className="text-xs text-white/40 mt-1">Paso {step} / 3</p>
          </div>
          <button onClick={handleClose} className="p-2 rounded-full text-white/30 hover:text-white hover:bg-white/10 transition-colors"><X className="h-5 w-5"/></button>
        </div>

        <div className="p-8 overflow-y-auto custom-scrollbar">
          
          {step === 1 && (
            <div className="space-y-8">
              {/* Resumen Plan */}
              <div className="p-5 rounded-2xl bg-gradient-to-r from-indigo-900/20 to-purple-900/20 border border-indigo-500/20 flex justify-between items-center">
                 <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-indigo-300 mb-1">Plan Seleccionado</p>
                    <h3 className="text-2xl font-bold text-white">{plan?.name}</h3>
                    <p className="text-xs text-white/50">{plan?.description}</p>
                 </div>
                 <div className="text-right">
                    <p className="text-3xl font-light text-white">${plan?.price}</p>
                    <p className="text-xs font-mono text-emerald-400">Bs. {getBsPrice(plan?.price || 0).toLocaleString('es-VE', { minimumFractionDigits: 2 })}</p>
                 </div>
              </div>

              {/* Métodos */}
              <div>
                <h4 className="text-xs font-bold uppercase tracking-widest text-white/40 mb-4">Selecciona Pasarela</h4>
                {paymentMethods.length === 0 ? (
                  <div className="text-center py-12 border border-dashed border-white/10 rounded-2xl"><p className="text-white/30 text-sm">No hay métodos disponibles.</p></div>
                ) : (
                  <div className="grid gap-3">
                    {paymentMethods.map((method) => {
                      const Icon = PAYMENT_TYPE_ICONS[method.type] || CreditCard;
                      const isSelected = selectedMethod?.id === method.id;
                      
                      return (
                        <div key={method.id} className={`relative rounded-xl border transition-all overflow-hidden ${isSelected ? 'border-indigo-500 bg-indigo-500/10' : 'border-white/10 bg-white/5 hover:border-white/20'}`}>
                          <button onClick={() => setSelectedMethod(method)} className="w-full text-left p-4 flex items-center gap-4">
                              <div className={`p-3 rounded-lg ${isSelected ? 'bg-indigo-500 text-white' : 'bg-black/40 text-white/40'}`}><Icon className="h-6 w-6"/></div>
                              <div className="flex-1">
                                 <h5 className="font-bold text-white text-sm">{method.name}</h5>
                                 <p className="text-[10px] uppercase tracking-widest text-white/40">{method.type_display}</p>
                              </div>
                              {isSelected && <CheckCircle className="h-5 w-5 text-indigo-400"/>}
                          </button>
                          
                          {/* Detalles Expandidos */}
                          <div className={`border-t border-white/5 bg-black/20 transition-all duration-300 ${isSelected ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
                             <div className="p-4 space-y-3">
                                {method.type === 'bank_transfer' && (
                                    <>
                                      {method.config.banco && <DetailRow label="Banco" value={method.config.banco} onCopy={() => copyToClipboard(method.config.banco, 'banco')} copied={copiedField === 'banco'} />}
                                      {method.config.numero_cuenta && <DetailRow label="Cuenta" value={method.config.numero_cuenta} onCopy={() => copyToClipboard(method.config.numero_cuenta, 'cuenta')} copied={copiedField === 'cuenta'} isMono />}
                                      {method.config.titular && <DetailRow label="Titular" value={method.config.titular} onCopy={() => copyToClipboard(method.config.titular, 'titular')} copied={copiedField === 'titular'} />}
                                      {method.config.rif && <DetailRow label="RIF" value={method.config.rif} onCopy={() => copyToClipboard(method.config.rif, 'rif')} copied={copiedField === 'rif'} />}
                                    </>
                                )}
                                {method.type === 'mobile_payment' && (
                                    <>
                                      {method.config.banco && <DetailRow label="Banco" value={method.config.banco} onCopy={() => copyToClipboard(method.config.banco, 'banco')} copied={copiedField === 'banco'} />}
                                      {method.config.telefono && <DetailRow label="Teléfono" value={method.config.telefono} onCopy={() => copyToClipboard(method.config.telefono, 'telefono')} copied={copiedField === 'telefono'} isMono />}
                                      {method.config.cedula && <DetailRow label="Cédula" value={method.config.cedula} onCopy={() => copyToClipboard(method.config.cedula, 'cedula')} copied={copiedField === 'cedula'} />}
                                    </>
                                )}
                                {(method.type === 'zelle' || method.type === 'paypal' || method.type === 'zinli') && method.config.email && (
                                    <DetailRow label="Email" value={method.config.email} onCopy={() => copyToClipboard(method.config.email, 'email')} copied={copiedField === 'email'} />
                                )}
                                {method.type === 'wally' && method.config.telefono && (
                                    <DetailRow label="Teléfono" value={method.config.telefono} onCopy={() => copyToClipboard(method.config.telefono, 'telefono')} copied={copiedField === 'telefono'} isMono />
                                )}
                                {(method.type === 'binance' || method.type === 'reserve') && (
                                    <>
                                      {method.config.pay_id && <DetailRow label="Pay ID" value={method.config.pay_id} onCopy={() => copyToClipboard(method.config.pay_id, 'pay_id')} copied={copiedField === 'pay_id'} isMono />}
                                      {method.config.email && <DetailRow label="Email" value={method.config.email} onCopy={() => copyToClipboard(method.config.email, 'email')} copied={copiedField === 'email'} />}
                                    </>
                                )}
                                {method.type === 'crypto' && (
                                    <>
                                      {method.config.coin && <DetailRow label="Moneda" value={method.config.coin} onCopy={() => copyToClipboard(method.config.coin, 'coin')} copied={copiedField === 'coin'} />}
                                      {method.config.network && <DetailRow label="Red" value={method.config.network} onCopy={() => copyToClipboard(method.config.network, 'network')} copied={copiedField === 'network'} />}
                                      {method.config.address && <DetailRow label="Address" value={method.config.address} onCopy={() => copyToClipboard(method.config.address, 'address')} copied={copiedField === 'address'} isMono />}
                                    </>
                                )}
                                <button onClick={(e) => { e.stopPropagation(); copyAllDetails(method); }} className="w-full py-2 mt-2 rounded-lg border border-white/10 bg-white/5 text-xs font-bold uppercase tracking-widest text-white/60 hover:text-white hover:bg-white/10 transition-all flex items-center justify-center gap-2">
                                   {copiedField === 'all' ? <Check className="h-3 w-3 text-green-400"/> : <Copy className="h-3 w-3"/>} Copiar Todo
                                </button>
                             </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex justify-end pt-4">
                <button onClick={() => setStep(2)} disabled={!selectedMethod} className="px-8 py-3 bg-white text-black text-xs font-bold uppercase tracking-widest rounded-full hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-[0_0_20px_rgba(255,255,255,0.2)]">
                  Siguiente Paso
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="p-4 rounded-xl bg-indigo-500/10 border border-indigo-500/20">
                 <h5 className="text-sm font-bold text-white mb-1">Confirmando Pago</h5>
                 <p className="text-xs text-white/50">Método: <span className="text-indigo-300">{selectedMethod?.name}</span></p>
              </div>

              {/* Mensaje de advertencia */}
              <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 flex gap-3">
                <AlertCircle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <h5 className="text-sm font-bold text-amber-300">Verifica tu comprobante</h5>
                  <p className="text-xs text-amber-200/70 leading-relaxed">
                    Asegúrate de que el comprobante sea legible y que el monto coincida exactamente con el precio del plan. 
                    Esto permitirá que tu pago sea aprobado automáticamente en segundos.
                  </p>
                </div>
              </div>

              <div>
                <label className={labelClass}>Comprobante</label>
                <div className="border-2 border-dashed border-white/10 rounded-2xl p-8 text-center hover:border-indigo-500/50 transition-colors bg-black/20">
                  {preview ? (
                    <div className="space-y-4">
                      <img src={preview} alt="Preview" className="max-h-48 mx-auto rounded-lg object-contain" />
                      <button type="button" onClick={() => { setFormData({ ...formData, receipt_image: null }); setPreview(null); }} className="text-xs font-bold text-red-400 hover:text-red-300 uppercase tracking-wide">Cambiar Archivo</button>
                    </div>
                  ) : (
                    <label className="cursor-pointer group">
                      <Upload className="h-10 w-10 text-white/20 mx-auto mb-4 group-hover:text-indigo-400 transition-colors" />
                      <span className="block text-sm font-bold text-white group-hover:text-indigo-300">Click para subir imagen</span>
                      <span className="text-xs text-white/30 mt-1 block">Máx. 10MB</span>
                      <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" required />
                    </label>
                  )}
                </div>
              </div>

              <div><label className={labelClass}>Número de Referencia</label><input type="text" value={formData.reference_number} onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })} className={inputClass} placeholder="Ej: 123456" /></div>
              <div><label className={labelClass}>Notas Adicionales</label><textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} className={`${inputClass} resize-none`} placeholder="Detalles opcionales..." /></div>

              <div className="flex items-center gap-4 pt-4 border-t border-white/10">
                <button type="button" onClick={() => setStep(1)} disabled={uploadStatus.isLoading} className="px-6 py-3 rounded-full border border-white/10 text-white/60 text-xs font-bold uppercase tracking-widest hover:text-white hover:bg-white/5 transition-all">Atrás</button>
                <button type="submit" disabled={uploadStatus.isLoading || !formData.receipt_image} className="flex-1 px-6 py-3 rounded-full bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold uppercase tracking-widest shadow-[0_0_20px_rgba(99,102,241,0.3)] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                  {uploadStatus.isLoading ? <><Loader2 className="h-4 w-4 animate-spin"/> {uploadStatus.message}</> : 'Confirmar Transacción'}
                </button>
              </div>

              {uploadStatus.isError && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs text-center">
                   {uploadStatus.errorMessage}
                </div>
              )}
            </form>
          )}

          {step === 3 && (
            <div className="text-center py-12 space-y-6">
              <div className="w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto border border-emerald-500/20 relative">
                 <div className="absolute inset-0 bg-emerald-500/20 rounded-full animate-ping opacity-50" />
                 <CheckCircle className="h-10 w-10 text-emerald-400 relative z-10" />
              </div>
              <div>
                 <h4 className="text-2xl font-bold text-white mb-2">¡Pago Recibido!</h4>
                 <p className="text-white/50 text-sm max-w-sm mx-auto leading-relaxed">
                    Tu comprobante está siendo verificado automáticamente. En pocos segundos tu plan será activado.
                 </p>
              </div>
              <button onClick={handleClose} className="px-8 py-3 bg-white text-black text-xs font-bold uppercase tracking-widest rounded-full hover:bg-gray-200 transition-colors shadow-lg">
                Entendido
              </button>
            </div>
          )}

        </div>
      </div>

      {/* Modal de Confirmación */}
      {showConfirmModal && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-[#0F0F0F] border border-white/10 rounded-2xl p-8 max-w-md mx-4 space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-amber-500/20">
                <AlertCircle className="h-8 w-8 text-amber-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Confirma tu pago</h3>
              <div className="space-y-3 text-left">
                <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                  <p className="text-xs text-white/40 mb-1">Plan</p>
                  <p className="text-sm font-bold text-white">{plan?.name}</p>
                </div>
                <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                  <p className="text-xs text-white/40 mb-1">Monto a pagar</p>
                  <p className="text-lg font-bold text-white">
                    ${plan?.price} USD
                    <span className="text-sm font-normal text-emerald-400 ml-2">
                      (Bs. {getBsPrice(plan?.price || 0).toLocaleString('es-VE', { minimumFractionDigits: 2 })})
                    </span>
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                  <p className="text-xs text-white/40 mb-1">Método de pago</p>
                  <p className="text-sm font-bold text-white">{selectedMethod?.name}</p>
                </div>
              </div>
              <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <p className="text-xs text-amber-200/80 leading-relaxed">
                  ¿El comprobante que subiste muestra este monto exacto? Verifica que sea legible y correcto para aprobación automática.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 px-6 py-3 rounded-full border border-white/10 text-white/60 text-xs font-bold uppercase tracking-widest hover:text-white hover:bg-white/5 transition-all"
              >
                Revisar
              </button>
              <button
                type="button"
                onClick={handleConfirmPayment}
                className="flex-1 px-6 py-3 rounded-full bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold uppercase tracking-widest shadow-[0_0_20px_rgba(99,102,241,0.3)] transition-all"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Subcomponente para filas de detalles
const DetailRow = ({ label, value, onCopy, copied, isMono }) => (
   <div className="flex items-center justify-between group">
      <div className="flex items-center gap-3 min-w-0">
         <span className="text-[10px] font-bold uppercase tracking-widest text-white/30 w-16 flex-shrink-0">{label}</span>
         <span className={`text-sm text-white truncate ${isMono ? 'font-mono' : ''}`}>{value}</span>
      </div>
      <button onClick={(e) => { e.stopPropagation(); onCopy(); }} className="p-1.5 rounded hover:bg-white/10 text-white/30 hover:text-white transition-colors opacity-0 group-hover:opacity-100">
         {copied ? <Check className="h-3 w-3 text-green-400"/> : <Copy className="h-3 w-3"/>}
      </button>
   </div>
);